import { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '@/components/feature/ProductCard';
import { products, type Product } from '@/mocks/products';

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedStyle, setSelectedStyle] = useState<string>('');
  const [selectedOccasion, setSelectedOccasion] = useState<string>('');
  const [sortBy, setSortBy] = useState<'relevance' | 'price-low' | 'price-high'>('relevance');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const categories = useMemo(() => {
    const cats = new Set(products.map((p) => p.category));
    return Array.from(cats);
  }, []);

  const styles = useMemo(() => {
    const sts = new Set(products.map((p) => p.style));
    return Array.from(sts);
  }, []);

  const occasions = useMemo(() => {
    const occs = new Set(products.map((p) => p.occasion));
    return Array.from(occs);
  }, []);

  const results = useMemo(() => {
    let filtered: Product[] = [...products];

    if (query.trim()) {
      const q = query.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.style.toLowerCase().includes(q) ||
          p.occasion.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q)
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }
    if (selectedStyle) {
      filtered = filtered.filter((p) => p.style === selectedStyle);
    }
    if (selectedOccasion) {
      filtered = filtered.filter((p) => p.occasion === selectedOccasion);
    }

    if (sortBy === 'price-low') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => b.price - a.price);
    }

    return filtered;
  }, [query, selectedCategory, selectedStyle, selectedOccasion, sortBy]);

  const activeFiltersCount =
    (selectedCategory ? 1 : 0) + (selectedStyle ? 1 : 0) + (selectedOccasion ? 1 : 0);

  const clearAllFilters = () => {
    setSelectedCategory('');
    setSelectedStyle('');
    setSelectedOccasion('');
  };

  return (
    <main className="min-h-screen bg-background-50">
      {/* Search bar section */}
      <section className="pt-24 pb-8 bg-primary-500">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-6 text-center">
            Search
          </h1>

          <div className="relative">
            <div className="w-5 h-5 flex items-center justify-center absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <i className="ri-search-line text-lg text-foreground-400"></i>
            </div>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products, styles, occasions..."
              className="w-full pl-12 pr-10 py-3.5 rounded-xl bg-background-50 text-foreground-800 font-body text-sm placeholder:text-foreground-400 border-2 border-transparent focus:border-accent-500 focus:outline-none transition-colors"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-close-line"></i>
                </div>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Filters + Results */}
      <section className="py-8">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          {/* Filters row */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            {/* Category */}
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="appearance-none bg-background-100 text-foreground-700 font-label text-xs font-medium py-2 pl-3 pr-8 rounded-lg border border-background-200/60 cursor-pointer hover:bg-background-200/70 transition-colors focus:outline-none focus:border-accent-500"
              >
                <option value="">Category</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <div className="w-3.5 h-3.5 flex items-center justify-center absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <i className="ri-arrow-down-s-line text-foreground-400 text-xs"></i>
              </div>
            </div>

            {/* Style */}
            <div className="relative">
              <select
                value={selectedStyle}
                onChange={(e) => setSelectedStyle(e.target.value)}
                className="appearance-none bg-background-100 text-foreground-700 font-label text-xs font-medium py-2 pl-3 pr-8 rounded-lg border border-background-200/60 cursor-pointer hover:bg-background-200/70 transition-colors focus:outline-none focus:border-accent-500"
              >
                <option value="">Style</option>
                {styles.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <div className="w-3.5 h-3.5 flex items-center justify-center absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <i className="ri-arrow-down-s-line text-foreground-400 text-xs"></i>
              </div>
            </div>

            {/* Occasion */}
            <div className="relative">
              <select
                value={selectedOccasion}
                onChange={(e) => setSelectedOccasion(e.target.value)}
                className="appearance-none bg-background-100 text-foreground-700 font-label text-xs font-medium py-2 pl-3 pr-8 rounded-lg border border-background-200/60 cursor-pointer hover:bg-background-200/70 transition-colors focus:outline-none focus:border-accent-500"
              >
                <option value="">Occasion</option>
                {occasions.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
              <div className="w-3.5 h-3.5 flex items-center justify-center absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <i className="ri-arrow-down-s-line text-foreground-400 text-xs"></i>
              </div>
            </div>

            {/* Clear filters */}
            {activeFiltersCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-xs font-label font-medium text-accent-500 hover:text-accent-600 cursor-pointer whitespace-nowrap transition-colors"
              >
                Clear filters ({activeFiltersCount})
              </button>
            )}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Sort */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="appearance-none bg-background-100 text-foreground-700 font-label text-xs font-medium py-2 pl-3 pr-8 rounded-lg border border-background-200/60 cursor-pointer hover:bg-background-200/70 transition-colors focus:outline-none focus:border-accent-500"
              >
                <option value="relevance">Sort: Relevance</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
              <div className="w-3.5 h-3.5 flex items-center justify-center absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <i className="ri-arrow-down-s-line text-foreground-400 text-xs"></i>
              </div>
            </div>
          </div>

          {/* Results count */}
          <p className="text-sm font-body text-foreground-500 mb-6">
            {query.trim()
              ? `${results.length} result${results.length !== 1 ? 's' : ''} for "${query}"`
              : `Showing all ${results.length} product${results.length !== 1 ? 's' : ''}`}
          </p>

          {/* Results grid */}
          {results.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {results.map((product) => (
                <ProductCard key={product.id} product={product} compact />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-4xl text-foreground-300"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground-600 mb-2">
                No products found
              </h3>
              <p className="text-sm font-body text-foreground-400 mb-6 max-w-md mx-auto">
                Try adjusting your search or filters — we might have something else you'll love.
              </p>
              <button
                onClick={() => {
                  setQuery('');
                  clearAllFilters();
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 font-label font-semibold text-xs rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-refresh-line"></i>
                </div>
                View All Products
              </button>
            </div>
          )}

          {/* Quick links when no query */}
          {!query && results.length > 0 && (
            <div className="mt-12 pt-8 border-t border-background-200/60">
              <p className="text-xs font-label font-semibold uppercase tracking-wider text-foreground-400 mb-4">
                Popular Searches
              </p>
              <div className="flex flex-wrap gap-2">
                {['Earrings', 'Chains', 'Gold', 'Minimal', 'Gifting', 'Pearl'].map((term) => (
                  <button
                    key={term}
                    onClick={() => setQuery(term)}
                    className="px-4 py-2 bg-background-100 text-foreground-700 font-label text-xs font-medium rounded-full hover:bg-secondary-100 hover:text-secondary-900 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}